import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Link2, Unlink, Undo2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MoveDialog, TransfersPanel } from "@/components/inventory/HolderMoves";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const SimLink = ({ s, onDone }) => {
  const [opts, setOpts] = useState(null);
  const load = () => api.get(`/inventory/serials/${s.id}/sim-options`).then((r) => setOpts(r.data)).catch((e) => toast.error(errMsg(e)));
  const link = async (sid) => {
    try { await api.post(`/inventory/serials/${s.id}/sim`, { sim_serial_id: sid }); toast.success("SIM asociada"); setOpts(null); onDone(); }
    catch (e) { toast.error(errMsg(e)); load(); }
  };
  const unlink = async () => {
    try { await api.delete(`/inventory/serials/${s.id}/sim`); toast.success("SIM desasociada"); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <span className="inline-flex flex-wrap items-center gap-1" data-testid={`sim-link-${s.serial}`}>
      {s.sim_iccid && <span className="rounded border border-emerald-500/40 px-1.5 text-emerald-400" data-testid={`sim-prepared-${s.serial}`}>Preparado con SIM {s.sim_iccid}</span>}
      {opts === null
        ? <Button size="sm" variant="ghost" className="h-6 px-2" onClick={load} data-testid={`sim-link-open-${s.serial}`}><Link2 className="mr-1 h-3 w-3" />{s.sim_iccid ? "Cambiar SIM" : "Asociar SIM"}</Button>
        : <select className="h-7 rounded border border-border bg-background px-1" defaultValue="" onChange={(e) => e.target.value && link(e.target.value)} data-testid={`sim-link-select-${s.serial}`}>
          <option value="">{opts.length ? "Elige SIM (ICCID)…" : "Sin SIM disponibles en la misma ubicación/responsable"}</option>
          {opts.map((o) => <option key={o.id} value={o.id}>{o.serial}{o.carrier ? ` · ${o.carrier}` : ""}{o.phone ? ` · ${o.phone}` : ""}</option>)}
        </select>}
      {s.sim_iccid && <Button size="sm" variant="ghost" className="h-6 px-2 text-red-400" onClick={unlink} data-testid={`sim-unlink-${s.serial}`}><Unlink className="mr-1 h-3 w-3" />Quitar</Button>}
    </span>
  );
};

export const serialInfo = (s) => [s.carrier, s.phone && `Tel. ${s.phone}`, s.associated_gps_serial && `En GPS ${s.associated_gps_serial}`].filter(Boolean).join(" · ");

const MoveButtons = ({ id, onPick, returnOnly }) => (
  <span className="inline-flex gap-1">
    <Button size="sm" variant="outline" className="h-6 px-2" onClick={() => onPick("return")} data-testid={`holder-return-${id}`}><Undo2 className="mr-1 h-3 w-3" />{returnOnly ? "Devolver al almacén" : "Devolver"}</Button>
    {!returnOnly && <Button size="sm" variant="outline" className="h-6 px-2" onClick={() => onPick("transfer")} data-testid={`holder-transfer-${id}`}><Send className="mr-1 h-3 w-3" />Transferir</Button>}
  </span>
);

export const HolderInventory = ({ userId }) => {
  const [d, setD] = useState(null);
  const [mv, setMv] = useState(null);
  const [ver, setVer] = useState(0);
  const { can } = useAuth();
  const self = userId === "me";
  const canMove = self || can("inventory.write");
  const load = useCallback(() => userId && api.get(`/inventory/holders/${userId}`).then((r) => { setD(r.data); setVer((v) => v + 1); }).catch((e) => toast.error(errMsg(e))), [userId]);
  useEffect(() => { setD(null); load(); }, [load]);
  if (!userId) return null;
  if (!d) return <p className="text-xs text-muted-foreground">Cargando…</p>;
  return (
    <div className="space-y-3 text-sm" data-testid="holder-inventory">
      <TransfersPanel userId={userId} version={ver} onChange={load} />
      {!d.serials.length && !d.quantities.length && <p className="text-muted-foreground" data-testid="holder-empty">Sin artículos bajo su responsabilidad.</p>}
      {d.serials.map((s) => (
        <div key={s.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border px-3 py-2" data-testid={`holder-serial-${s.serial}`}>
          <div><div className="font-mono">{s.serial}</div><div className="text-xs text-muted-foreground">{s.item_label}{serialInfo(s) ? ` · ${serialInfo(s)}` : ""}</div></div>
          {s.pending_transfer_id ? <span className="rounded border border-amber-500/40 px-1.5 text-xs text-amber-400" data-testid={`holder-pending-${s.serial}`}>Transferencia pendiente</span>
            : <span className="inline-flex flex-wrap gap-1">
              {s.role === "gps" && <SimLink s={s} onDone={load} />}
              {canMove && !s.associated_gps_id && <MoveButtons id={s.serial} returnOnly={!self} onPick={(mode) => setMv({ mode, item_id: s.item_id, serial: s.serial, sim: s.sim_iccid, label: s.item_label })} />}
            </span>}
        </div>))}
      {d.quantities.map((q) => <div key={q.item_id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border px-3 py-2" data-testid={`holder-qty-${q.item_id}`}>
        <span>{q.item_label}</span>
        <span className="flex items-center gap-2"><span className="font-mono">{q.quantity} {q.unit}{q.pending_out > 0 && <span className="ml-1 text-xs text-amber-400">({q.pending_out} en transferencia)</span>}</span>
          {canMove && q.quantity - q.pending_out > 0 && <MoveButtons id={q.item_id} returnOnly={!self} onPick={(mode) => setMv({ mode, item_id: q.item_id, max: q.quantity - q.pending_out, label: q.item_label })} />}</span>
      </div>)}
      <MoveDialog target={mv} holderId={userId} onClose={() => setMv(null)} onDone={load} />
    </div>
  );
};
